import DOMPurify from "dompurify";
import { useEffect, useState } from "react";
import AdminLayout from "@/components/layout/AdminLayout";
import { api, formatApiError } from "@/lib/api";
import { Save, History, RotateCcw, Eye } from "lucide-react";

export default function AdminPrivacyPolicy() {
  const [current, setCurrent] = useState(null);
  const [versions, setVersions] = useState([]);
  const [draft, setDraft] = useState({ title: "", summary: "", body_html: "", effective_date: "", note: "" });
  const [saving, setSaving] = useState(false);
  const [previewOn, setPreviewOn] = useState(false);
  const [error, setError] = useState("");
  const [savedAt, setSavedAt] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/privacy-policy");
      setCurrent(data.current || null);
      setVersions(data.versions || []);
      if (data.current) {
        setDraft({
          title: data.current.title || "",
          summary: data.current.summary || "",
          body_html: data.current.body_html || "",
          effective_date: data.current.effective_date || "",
          note: "",
        });
      }
    } catch (e) { setError(formatApiError(e)); }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const save = async () => {
    setSaving(true);
    setError("");
    try {
      const { data } = await api.put("/admin/privacy-policy", draft);
      setSavedAt(new Date());
      setCurrent(data);
      setDraft((d) => ({ ...d, note: "" }));
      await load();
    } catch (e) { setError(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const rollback = async (versionId) => {
    if (!window.confirm("Restore this version as the published Privacy Policy?")) return;
    try {
      await api.post(`/admin/privacy-policy/rollback/${versionId}`);
      await load();
    } catch (e) { setError(formatApiError(e)); }
  };

  return (
    <AdminLayout
      title="Privacy Policy"
      subtitle="Edit the public Privacy Policy. Each save creates a new version with full audit history and rollback."
      actions={
        <>
          <button onClick={() => setPreviewOn((v) => !v)} className="btn-secondary" data-testid="privacy-preview-toggle">
            <Eye className="h-4 w-4" /> {previewOn ? "Hide preview" : "Preview"}
          </button>
          <button onClick={save} disabled={saving} className="btn-primary disabled:opacity-60" data-testid="privacy-save">
            <Save className="h-4 w-4" />
            {saving ? "Publishing..." : "Publish new version"}
          </button>
        </>
      }
    >
      {error && <div className="mb-4 text-sm text-destructive" data-testid="privacy-error">{error}</div>}
      {savedAt && <div className="mb-4 text-xs font-mono text-execution">Published v{current?.version} at {savedAt.toLocaleTimeString()}</div>}

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
        <div className="lg:col-span-8 space-y-4">
          <div className="border border-border bg-white p-5 space-y-4" data-testid="privacy-editor">
            <div className="overline">Current version: {current?.version || "—"}</div>
            <label className="block">
              <span className="block text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground mb-2">Title</span>
              <input
                className="input-swiss"
                value={draft.title}
                onChange={(e) => setDraft((d) => ({ ...d, title: e.target.value }))}
                data-testid="privacy-title"
              />
            </label>
            <label className="block">
              <span className="block text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground mb-2">Effective date</span>
              <input
                type="date"
                className="input-swiss"
                value={draft.effective_date}
                onChange={(e) => setDraft((d) => ({ ...d, effective_date: e.target.value }))}
                data-testid="privacy-effective-date"
              />
            </label>
            <label className="block">
              <span className="block text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground mb-2">Summary</span>
              <textarea
                className="input-swiss min-h-[80px]"
                value={draft.summary}
                onChange={(e) => setDraft((d) => ({ ...d, summary: e.target.value }))}
                data-testid="privacy-summary"
              />
            </label>
            <label className="block">
              <span className="block text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground mb-2">Body (HTML)</span>
              <textarea
                className="input-swiss font-mono text-xs min-h-[500px] resize-y"
                value={draft.body_html}
                onChange={(e) => setDraft((d) => ({ ...d, body_html: e.target.value }))}
                data-testid="privacy-body"
              />
            </label>
            <label className="block">
              <span className="block text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground mb-2">Change note (shown in history)</span>
              <input
                className="input-swiss"
                placeholder="What changed?"
                value={draft.note}
                onChange={(e) => setDraft((d) => ({ ...d, note: e.target.value }))}
                data-testid="privacy-note"
              />
            </label>
          </div>

          {previewOn && (
            <div className="border border-border bg-white" data-testid="privacy-preview-panel">
              <div className="px-5 py-3 border-b border-border overline">Preview</div>
              <div className="p-6 prose-policy" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(draft.body_html || "") }} />
            </div>
          )}
        </div>

        <aside className="lg:col-span-4" data-testid="privacy-versions">
          <div className="border border-border bg-white">
            <div className="px-5 py-3 border-b border-border flex items-center gap-2">
              <History className="h-4 w-4 text-muted-foreground" />
              <span className="overline">Revision history</span>
            </div>
            <ul className="divide-y divide-border max-h-[640px] overflow-y-auto">
              {versions.length === 0 && (
                <li className="p-5 text-sm text-muted-foreground">No versions yet.</li>
              )}
              {versions.map((v) => (
                <li key={v.id} className="p-4" data-testid={`privacy-version-${v.version}`}>
                  <div className="flex items-center justify-between gap-2">
                    <div>
                      <div className="font-mono text-sm">
                        v{v.version}
                        {v.is_current && <span className="ml-2 chip bg-[#00C779] text-white border-[#00C779]">current</span>}
                      </div>
                      <div className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground mt-1">
                        {new Date(v.created_at).toLocaleString()} · {v.created_by}
                      </div>
                      {v.note && <div className="text-xs text-muted-foreground mt-2">{v.note}</div>}
                    </div>
                    {!v.is_current && (
                      <button
                        onClick={() => rollback(v.id)}
                        className="text-xs font-mono uppercase tracking-wider text-execution hover:underline shrink-0"
                        data-testid={`privacy-rollback-${v.version}`}
                      >
                        <RotateCcw className="h-3 w-3 inline mr-1" /> Restore
                      </button>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </aside>
      </div>
    </AdminLayout>
  );
}
